import axios from 'axios';
import config from '../config'; // Import the base URL from the configuration file

/**
 * Processes an image using the API.
 * @param imageData - The base64 string of the image to be processed.
 * @param format - The desired output format (e.g., 'webp' or 'avif').
 * @param colorMode - The color processing mode ('grayscale' or 'color'). Defaults to 'color'.
 * @param imageWidth - Target image width in pixels (default: 96).
 * @param imageHeight - Target image height in pixels (default: 96).
 * @param maxBytes - Maximum file size in bytes (default: 900).
 * @returns A promise that resolves to the processed image as a Uint8Array.
 */
const processImage = async (
  imageData: string, 
  format: 'webp' | 'avif', 
  colorMode: 'grayscale' | 'color' = 'color',
  imageWidth?: number,
  imageHeight?: number,
  maxBytes?: number
): Promise<Uint8Array> => {
  try {
    // Convert the base64 image data to a Blob
    const byteString = atob(imageData.split(',')[1]);
    const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([arrayBuffer], { type: mimeString });

    // Determine the file extension based on the MIME type
    const fileExtension = mimeString.split('/')[1]; // e.g., 'jpeg' or 'png'

    // Create a FormData object
    const formData = new FormData();
    formData.append('file', blob, `image.${fileExtension}`); // Append the image file with the correct extension
    formData.append('dataFormat', format); // Append the data format
    formData.append('color_mode', colorMode); // Append the color mode
    
    // Append optional parameters if provided
    if (imageWidth !== undefined && imageWidth !== null) {
      formData.append('image_width', imageWidth.toString());
    }
    if (imageHeight !== undefined && imageHeight !== null) {
      formData.append('image_height', imageHeight.toString());
    }
    if (maxBytes !== undefined && maxBytes !== null) {
      formData.append('max_bytes', maxBytes.toString());
    }

    // Make the API call
    const response = await axios.post(`${config.baseURL}/extractFaceAndCompressImage`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'arraybuffer', // Expect the response as an ArrayBuffer
    });

    // Convert the ArrayBuffer to a Uint8Array and return it
    return new Uint8Array(response.data);
  } catch (error) {
    console.error('Error processing image:', error);
    throw new Error('Image processing failed');
  }
};

export default processImage;